import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { Component, OnInit } from '@angular/core';
import { Validators, UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import * as _ from 'lodash';
import { AppBarAction, AppBarTitleService, AppBarActionsService, MessageService, FormHelper, EnumHelper, AppPageService, IdentityVerificationStatus, IdentityVerificationStatusLabel, IdentityVerificationMethod, IdentityVerificationMethodLabel, IdentityVerificationFacade } from 'common';
import { CustomerComponentData } from '../customer.model';
import { UserPermissionService } from '../../user/user-permission/user-permission.service';
import { readWrite } from '../../user/user-permission/user-permission.data';
import { CustomerIdentityUpdateDialogComponent } from '../customer-identity-update-dialog/customer-identity-update-dialog.component';

@UntilDestroy()
@Component({
    selector: 'ifb-customer-identity-details',
    templateUrl: './customer-identity-details.component.html',
    styleUrls: ['./customer-identity-details.component.scss'],
    standalone: false
})
export class CustomerIdentityDetailsComponent implements OnInit {
  canEditIdentificationData = false;

  private saveSub: Subscription;
  form: UntypedFormGroup;
  data: CustomerComponentData;

  readonly statusOptions = EnumHelper.getMappedNamesAndValues(IdentityVerificationStatus, IdentityVerificationStatusLabel);
  readonly methodOptions = EnumHelper.getMappedNamesAndValues(IdentityVerificationMethod, IdentityVerificationMethodLabel);
  readonly IdentityVerificationMethod = IdentityVerificationMethod;

  constructor(
    private route: ActivatedRoute,
    private appBarTitleService: AppBarTitleService,
    private formBuilder: UntypedFormBuilder,
    private appBarActionsService: AppBarActionsService,
    private appPageService: AppPageService,
    private messageService: MessageService,
    private userPermissionService: UserPermissionService,
    private identityVerificationFacade: IdentityVerificationFacade,
    private dialog: MatDialog) {

    this.initForm();

    this.appBarActionsService.actions = [
      { id: 'cancel', label: 'Cancel', buttonType: 'button' },
      { id: 'save', label: 'Save', disabled: !this.form.dirty, buttonType: 'submit', buttonAppearance: 'flat', buttonColor: 'primary' }
    ];
  }

  ngOnInit(): void {
    this.userPermissionService.granted([readWrite('servicing-customers-verification-status')])
      .pipe(untilDestroyed(this))
      .subscribe(res => {
        this.canEditIdentificationData = res;
        if (this.canEditIdentificationData) {
          this.form.controls.status.setValidators(Validators.required);
          this.form.controls.method.setValidators(Validators.required);
          this.form.controls.status.enable();
          this.form.controls.method.enable();
        } else {
          this.form.controls.status.clearValidators();
          this.form.controls.method.clearValidators();
          this.form.controls.status.disable();
          this.form.controls.method.disable();
        }
      });

    this.route.data.pipe(untilDestroyed(this))
      .subscribe(this.initData.bind(this));

    this.appBarActionsService.invoking.pipe(untilDestroyed(this))
      .subscribe(this.actionDispatch.bind(this));

    this.form.statusChanges.pipe(untilDestroyed(this))
      .subscribe(this.actionUpdate.bind(this));

    this.watchFormChanges();
  }

  private watchFormChanges(): void {
    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.form.dirty) {
        this.appBarActionsService.enable('save', true);
      }
    });
  }

  private actionDispatch(action: AppBarAction): void {
    const actionHandler: (action: AppBarAction) => void = this[action.id].bind(this);
    actionHandler(action);
  }

  private actionUpdate(): void {
    this.appBarActionsService.label('save', 'Save');
  }

  cancel(): void {
    this.appPageService.back();
  }

  private initData(data: CustomerComponentData): void {
    this.data = data;
    this.appBarTitleService.title = 'Identity Verification';

    if (data.customer.identityVerification) {
      this.form.patchValue(data.customer.identityVerification);
    }

    this.form.markAsPristine();
    this.actionUpdate();
  }

  private initForm(): void {
    this.form = this.formBuilder.group({
      method: [{ value: undefined, disabled: !this.canEditIdentificationData }],
      status: [{ value: undefined, disabled: !this.canEditIdentificationData }],
    });
  }

  save(): void {
    FormHelper.showInvalidFormFields(this.form);
    if (this.saveSub || !this.canEditIdentificationData)
      return;
    if (!this.form.valid)
      return;

    CustomerIdentityUpdateDialogComponent.show(this.dialog, this.form)
      .subscribe(result => {
        if (result) {
          const payload = {
            method: this.form.controls.method.value,
            status: this.form.controls.status.value,
            customerId: this.data.customer.id
          };
          this.saveSub = this.identityVerificationFacade.updateIdentityVerification(payload)
            .subscribe(this.saveSuccessHandler.bind(this), this.saveErrorHandler.bind(this));
        }
      });
  }

  private saveSubClear(): void {
    if (this.saveSub)
      this.saveSub.unsubscribe();
    this.saveSub = null;
  }

  private saveErrorHandler(error: any): void {
    this.saveSubClear();
    this.messageService.error(error.error ? (error.error.Message ?? error.error) : error);
  }

  private saveSuccessHandler(): void {
    this.saveSubClear();
    this.appPageService.back();
  }
}
